import * as Headless from '@headlessui/react';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'react-toastify';
import type {
  Document,
  EmbeddedSignature,
  EmbeddedSignatureInvite,
  PatchedPatchedDocument,
  SignatureRequest,
} from '../../../js/api';
import {
  DocumentsService,
  DocumentStatusEnum,
  SignatureParticipantStatusEnum,
  SignatureRequestStatusEnum,
  SignerAudienceEnum,
} from '../../../js/api';
import { handleApiError } from '../../../js/app/utils/errorhandling';
import { useHasPermission } from '../hooks/useHasPermission';
import { useOpenDocument } from '../hooks/useOpenDocument';
import { staffDocumentReadPermission } from '../utils/permissions';
import { useAudience } from '../utils/workflowAccess';
import { Button } from './button';
import { ConfirmationDialog } from './confirmation-dialog';
import { DocumentEditDialog } from './document-edit-dialog';
import { DocumentReplaceDialog } from './document-replace-dialog';
import { DocumentSignDialog } from './document-sign-dialog';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from './dropdown';
import { RequestSignatureDialog } from './request-signature-dialog';
import { TooltippedText } from './tooltipped-text';

/**
 * What a user may do to a document, and the UI that does it — shared by the
 * Documents tab (every document on a context) and the in-context views that
 * surface one type where it belongs (see document-type-access.tsx). One set of
 * rules, one set of dialogs, so the two can never drift.
 */

// ---------------------------------------------------------------- capabilities

/**
 * May the current viewer download a document's file? The same rule the backend applies
 * to download / zip: the upload is finalized (confirmed or signed), the viewer's
 * audience is in the type's `allowed_audience_download`, and a STAFF viewer holds the
 * read code of a context the document hangs on (`contextType`, or `alsoContextType`
 * for documents attached to a second context, e.g. a survey response report). Gate
 * anything that hands out the file — such as selecting it for a zip — on this.
 */
export function useCanDownloadDocument(contextType: string, alsoContextType?: string) {
  const audience = useAudience();
  const canReadContext = useHasPermission(staffDocumentReadPermission(contextType));
  const canReadOtherContext = useHasPermission(
    staffDocumentReadPermission(alsoContextType ?? contextType)
  );
  const canStaffRead = canReadContext || canReadOtherContext;
  return (doc: Document): boolean =>
    (doc.status === DocumentStatusEnum.CONFIRMED || doc.status === DocumentStatusEnum.SIGNED) &&
    (doc.document_type?.allowed_audience_download ?? []).includes(audience) &&
    (audience !== 'STAFF' || canStaffRead);
}

export type DocumentCapabilities = {
  isConfirmed: boolean;
  /** The file can be viewed/downloaded once its upload is finalized. */
  isFinalized: boolean;
  isOrphaned: boolean;
  isOwner: boolean;
  signature: EmbeddedSignature | null | undefined;
  /**
   * A signature document: its type requires signatures, or it carries a signature
   * request. Its Sign / Request signature / Replace are the everyday actions, so the
   * Documents tab shows them as one-click buttons beside the ⋮ menu.
   */
  isSignatureDocument: boolean;
  signatureLocked: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canReplace: boolean;
  canSign: boolean;
  canRequestSignature: boolean;
};

export type DocumentViewer = {
  /** Who owns uploads in this context (the acting entity's id). */
  ownerId: string;
  /** The viewer's audience code (STAFF / HEALTHCARE_ENTITY / PROVIDER). */
  audience: string;
  /** Staff holding the signature-request update code. */
  canUpdateSignature: boolean;
};

// Staff delete regardless of ownership; once a signature is under
// way it also takes the signature-request update code (mirrors
// the backend staff destroy exception).
function computeCanDelete({
  doc,
  audience,
  isStaff,
  canUpdateSignature,
  signatureLocked,
  isOrphaned,
  isOwner,
}: {
  doc: Document;
  audience: string;
  isStaff: boolean;
  canUpdateSignature: boolean;
  signatureLocked: boolean;
  isOrphaned: boolean;
  isOwner: boolean;
}): boolean {
  if (isStaff) return !signatureLocked || canUpdateSignature;
  if (signatureLocked) return false;
  if (isOrphaned) return true;
  return isOwner && (doc.document_type?.allowed_audience_delete ?? []).includes(audience);
}

// Replace while unlocked and allowed for the audience — or, for staff holding
// the signature code, a signing/executed signature document, which restarts
// the signature process (mirrors the backend unlock).
function computeCanReplace({
  doc,
  audience,
  isConfirmed,
  isFinalized,
  signatureLocked,
  isStaffSigner,
  hasSignature,
}: {
  doc: Document;
  audience: string;
  isConfirmed: boolean;
  isFinalized: boolean;
  signatureLocked: boolean;
  isStaffSigner: boolean;
  hasSignature: boolean;
}): boolean {
  const audienceMayReplace = (doc.document_type?.allowed_audience_replace ?? []).includes(audience);
  return (
    (isConfirmed && !signatureLocked && audienceMayReplace) ||
    (isStaffSigner && hasSignature && isFinalized)
  );
}

export function getDocumentCapabilities(
  doc: Document,
  { ownerId, audience, canUpdateSignature }: DocumentViewer
): DocumentCapabilities {
  const isConfirmed = doc.status === DocumentStatusEnum.CONFIRMED;
  // A document's file can be viewed/downloaded once its upload is
  // finalized — CONFIRMED or SIGNED (SIGNED is just a finalized,
  // locked document; a partially-signed doc is still CONFIRMED).
  // Viewing/downloading is governed by the document type (enforced by
  // the backend), never by signature state — signatures only affect
  // mutability (see signatureLocked below).
  const isFinalized = isConfirmed || doc.status === DocumentStatusEnum.SIGNED;
  const isOrphaned =
    doc.status === DocumentStatusEnum.PENDING || doc.status === DocumentStatusEnum.FAILED;
  const isOwner = doc.owner_id === ownerId;
  const signature = doc.signature;
  // A document locks the moment a signature is under way — as soon as
  // one party signs (PARTIALLY_SIGNED), not only when fully executed —
  // so its content can't change out from under a signer. Mirrors the
  // backend HasDocumentContextPermission lock.
  const signatureLocked =
    signature?.status === SignatureRequestStatusEnum.PARTIALLY_SIGNED ||
    signature?.status === SignatureRequestStatusEnum.COMPLETED ||
    doc.status === DocumentStatusEnum.SIGNED;
  const isStaff = audience === 'STAFF';
  const isStaffSigner = isStaff && canUpdateSignature;
  const canEdit = isOwner && !signatureLocked; // partial_update requires ownership
  const canDelete = computeCanDelete({
    doc,
    audience,
    isStaff,
    canUpdateSignature,
    signatureLocked,
    isOrphaned,
    isOwner,
  });
  const canReplace = computeCanReplace({
    doc,
    audience,
    isConfirmed,
    isFinalized,
    signatureLocked,
    isStaffSigner,
    hasSignature: !!signature,
  });
  const canSign = !!signature?.viewer_can_sign;
  // Staff can nudge the organization while its signer slot is open.
  const canRequestSignature =
    isStaffSigner &&
    !!signature &&
    signature.pending_signer_audiences.includes('HEALTHCARE_ENTITY');
  return {
    isConfirmed,
    isFinalized,
    isOrphaned,
    isOwner,
    signature,
    isSignatureDocument: !!doc.document_type?.require_signature || !!signature,
    signatureLocked,
    canEdit,
    canDelete,
    canReplace,
    canSign,
    canRequestSignature,
  };
}

// ---------------------------------------------------------------- signatures

/**
 * Magic-link invites to surface on the signature badge tooltip: only while
 * signatures are still being collected, and only for signers who haven't
 * signed yet (a signed participant's invite is moot).
 */
export function pendingSignerInvites(
  signature: Pick<EmbeddedSignature, 'status' | 'invites'> | null | undefined
): EmbeddedSignatureInvite[] {
  if (
    signature?.status !== SignatureRequestStatusEnum.PENDING &&
    signature?.status !== SignatureRequestStatusEnum.PARTIALLY_SIGNED
  ) {
    return [];
  }
  return (signature.invites ?? []).filter(
    (invite) => invite.participant_status !== SignatureParticipantStatusEnum.SIGNED
  );
}

/**
 * After a signature, whether to prompt the signer to invite the organization's
 * signer: true only when a STAFF member just signed and the HEALTHCARE_ENTITY
 * slot is still unsigned (per the signing design).
 */
export function shouldPromptOrgSigner(
  audience: string,
  signatureRequest: Pick<SignatureRequest, 'participants'>
): boolean {
  if (audience !== 'STAFF') return false;
  return (signatureRequest.participants ?? []).some(
    (p) =>
      p.signer_audience === SignerAudienceEnum.HEALTHCARE_ENTITY &&
      p.status !== SignatureParticipantStatusEnum.SIGNED
  );
}

// ---------------------------------------------------------------- action state

type UseDocumentActionsArgs = {
  /** Refetch the documents the caller renders. */
  invalidate: () => void;
  /** The parent may need to refetch its own queries (e.g. a signed ACL confirms its assignment). */
  onDocumentsChanged?: () => void;
};

/** Dialog state + mutations behind the action menu, one instance per rendering surface. */
export function useDocumentActions({ invalidate, onDocumentsChanged }: UseDocumentActionsArgs) {
  const [docPendingDelete, setDocPendingDelete] = useState<Document | null>(null);
  const [docEditing, setDocEditing] = useState<Document | null>(null);
  const [docPendingReplace, setDocPendingReplace] = useState<Document | null>(null);
  const [docSigning, setDocSigning] = useState<Document | null>(null);
  const [docRequestingSignature, setDocRequestingSignature] = useState<Document | null>(null);

  const { openDocument, isPending: isOpening } = useOpenDocument('Failed to download document');

  const patchDocumentMutation = useMutation({
    mutationFn: async (input: { id: string; requestBody: PatchedPatchedDocument }) =>
      DocumentsService.documentsPartialUpdate(input),
    onSuccess: () => {
      toast.success('Document updated');
      invalidate();
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Failed to update document', handle403: true });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => DocumentsService.documentsDestroy({ id }),
    onSuccess: () => {
      toast.success('Document removed');
      setDocPendingDelete(null);
      invalidate();
      onDocumentsChanged?.();
    },
    onError: (error: Error) => {
      handleApiError(error, { messagePrefix: 'Failed to remove document', handle403: true });
    },
  });

  const confirmDelete = async () => {
    if (!docPendingDelete) return;
    try {
      await deleteMutation.mutateAsync(docPendingDelete.id);
    } catch {
      // Toast already shown by deleteMutation.onError
    } finally {
      setDocPendingDelete(null);
    }
  };

  // Submit handler for the edit dialog (label + dates). Closes the dialog on
  // settle; the mutation's onError handles the toast.
  const handleEditSubmit = async (payload: PatchedPatchedDocument) => {
    if (!docEditing) return;
    try {
      await patchDocumentMutation.mutateAsync({ id: docEditing.id, requestBody: payload });
    } catch {
      // Toast already shown by patchDocumentMutation.onError
    } finally {
      setDocEditing(null);
    }
  };

  return {
    openDocument,
    isOpening,
    docPendingDelete,
    setDocPendingDelete,
    docEditing,
    setDocEditing,
    docPendingReplace,
    setDocPendingReplace,
    docSigning,
    setDocSigning,
    docRequestingSignature,
    setDocRequestingSignature,
    patchDocumentMutation,
    deleteMutation,
    confirmDelete,
    handleEditSubmit,
    invalidate,
    onDocumentsChanged,
  };
}

export type DocumentActionsState = ReturnType<typeof useDocumentActions>;

// ---------------------------------------------------------------- menu

/** Trigger / panel styling of the per-row action menus, shared so sibling menus
 *  (e.g. a card's entry menu beside the paperclip) look identical. `!rounded-md`:
 *  Bootstrap's unlayered reboot (`button { border-radius: 0 }`) beats the plain utility
 *  and leaves the trigger square. */
export const menuButtonClasses =
  'tw:inline-flex tw:items-center tw:justify-center tw:!rounded-md tw:p-1.5 tw:text-zinc-500 tw:hover:bg-brand-green-0 tw:hover:text-white tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-brand-green-0';
// The panel opens over white cards/tables; a light brand wash makes it pop.
export const menuPanelClasses = 'tw:z-[70] tw:!bg-brand-green-3';
/** A document's name as a one-click view link (the Documents tab, the in-context views). */
export const documentLinkClasses = 'tw:text-brand-green-0 tw:hover:underline tw:text-left';

type DocumentActionsMenuProps = {
  doc: Document;
  capabilities: DocumentCapabilities;
  actions: DocumentActionsState;
  /** Vertical ellipsis (the tab's menu) or a paperclip (in-context views). */
  trigger?: 'ellipsis' | 'paperclip';
  /**
   * Leave "Download Document" out: the row carries a checkbox and the selection bar
   * downloads (one) or zips (several), so a second download path would only add noise.
   */
  hideDownload?: boolean;
  /**
   * Leave a signature document's Sign / Request signature / Replace out: the caller
   * renders them as one-click buttons beside the menu (`DocumentQuickActions`). Other
   * documents keep every action in the menu.
   */
  quickActionsOutside?: boolean;
};

/** Every action the viewer may take on the document, in one dropdown. */
export function DocumentActionsMenu({
  doc,
  capabilities,
  actions,
  trigger = 'ellipsis',
  hideDownload = false,
  quickActionsOutside = false,
}: DocumentActionsMenuProps) {
  const { isFinalized, canEdit, canDelete } = capabilities;
  const inMenu = !(quickActionsOutside && capabilities.isSignatureDocument);
  const canReplace = inMenu && capabilities.canReplace;
  const canSign = inMenu && capabilities.canSign;
  const canRequestSignature = inMenu && capabilities.canRequestSignature;
  const hasAnyAction =
    isFinalized || canEdit || canReplace || canSign || canRequestSignature || canDelete;
  if (!hasAnyAction) return null;
  return (
    <Dropdown>
      <TooltippedText tooltipContent="Document actions" spanClassName="tw:inline-flex">
        <DropdownButton
          as="button"
          className={menuButtonClasses}
          aria-label="Actions"
          data-testid={`document-actions-menu-${doc.id}`}
        >
          {trigger === 'paperclip' ? (
            // in-context: brand green like the surrounding links
            <i className="fas fa-fw fa-paperclip tw:text-brand-green-0" />
          ) : (
            <i className="fas fa-fw fa-ellipsis-vertical" />
          )}
        </DropdownButton>
      </TooltippedText>
      <Headless.Portal>
        <DropdownMenu anchor="bottom end" className={menuPanelClasses}>
          {isFinalized && (
            <DropdownItem
              onClick={() => actions.openDocument(doc.id, 'inline')}
              data-testid={`document-menu-view-${doc.id}`}
            >
              <i data-slot="icon" className="fas fa-fw fa-eye" />
              View Document
            </DropdownItem>
          )}
          {isFinalized && !hideDownload && (
            <DropdownItem
              onClick={() => actions.openDocument(doc.id, 'attachment')}
              data-testid={`document-menu-download-${doc.id}`}
            >
              <i data-slot="icon" className="fas fa-fw fa-download" />
              Download Document
            </DropdownItem>
          )}
          {canEdit && (
            <DropdownItem onClick={() => actions.setDocEditing(doc)}>
              <i data-slot="icon" className="fas fa-fw fa-pencil" />
              Edit Document
            </DropdownItem>
          )}
          {canReplace && (
            <DropdownItem onClick={() => actions.setDocPendingReplace(doc)}>
              <i data-slot="icon" className="fas fa-fw fa-arrows-rotate" />
              Replace Document
            </DropdownItem>
          )}
          {canSign && (
            <DropdownItem onClick={() => actions.setDocSigning(doc)}>
              <i data-slot="icon" className="fas fa-fw fa-signature" />
              Sign Document
            </DropdownItem>
          )}
          {canRequestSignature && (
            <DropdownItem onClick={() => actions.setDocRequestingSignature(doc)}>
              <i data-slot="icon" className="fas fa-fw fa-paper-plane" />
              Request signature
            </DropdownItem>
          )}
          {canDelete && (
            <DropdownItem onClick={() => actions.setDocPendingDelete(doc)}>
              <i data-slot="icon" className="fa-regular fa-fw fa-trash tw:!text-stoplight-danger" />
              <span className="tw:text-stoplight-danger">Remove Document</span>
            </DropdownItem>
          )}
        </DropdownMenu>
      </Headless.Portal>
    </Dropdown>
  );
}

// ---------------------------------------------------------------- quick actions

/**
 * A signature document's everyday actions as one-click buttons beside the ⋮ menu: Sign,
 * Request signature and Replace, each only when the viewer may. Renders nothing for any
 * other document (those keep every action in the menu) or when none applies.
 */
export function DocumentQuickActions({
  doc,
  capabilities,
  actions,
}: Pick<DocumentActionsMenuProps, 'doc' | 'capabilities' | 'actions'>) {
  const { isSignatureDocument, canReplace, canSign, canRequestSignature } = capabilities;
  if (!isSignatureDocument || !(canReplace || canSign || canRequestSignature)) return null;
  const name = doc.label || 'document';
  return (
    <span
      className="tw:flex tw:items-center tw:gap-1"
      data-testid={`document-quick-actions-${doc.id}`}
    >
      {canSign && (
        <TooltippedText tooltipContent="Sign document" spanClassName="tw:inline-flex">
          <Button
            color="brandgreen0"
            small
            onClick={() => actions.setDocSigning(doc)}
            aria-label={`Sign ${name}`}
            data-testid={`document-sign-${doc.id}`}
          >
            <i className="fas fa-fw fa-signature" />
          </Button>
        </TooltippedText>
      )}
      {canRequestSignature && (
        <TooltippedText tooltipContent="Request signature" spanClassName="tw:inline-flex">
          <Button
            color="brand-green-outline"
            small
            onClick={() => actions.setDocRequestingSignature(doc)}
            aria-label={`Request signature for ${name}`}
            data-testid={`document-request-signature-${doc.id}`}
          >
            <i className="fas fa-fw fa-paper-plane" />
          </Button>
        </TooltippedText>
      )}
      {canReplace && (
        <TooltippedText tooltipContent="Replace document" spanClassName="tw:inline-flex">
          <Button
            color="brand-green-outline"
            small
            onClick={() => actions.setDocPendingReplace(doc)}
            aria-label={`Replace ${name}`}
            data-testid={`document-replace-${doc.id}`}
          >
            <i className="fas fa-fw fa-arrows-rotate" />
          </Button>
        </TooltippedText>
      )}
    </span>
  );
}

// ---------------------------------------------------------------- dialogs

/** The confirm / edit / replace / sign / request-signature dialogs behind the actions. */
export function DocumentActionDialogs({
  actions,
  audience,
}: {
  actions: DocumentActionsState;
  audience: string;
}) {
  const {
    docPendingDelete,
    setDocPendingDelete,
    docEditing,
    setDocEditing,
    docPendingReplace,
    setDocPendingReplace,
    docSigning,
    setDocSigning,
    docRequestingSignature,
    setDocRequestingSignature,
    patchDocumentMutation,
    deleteMutation,
    confirmDelete,
    handleEditSubmit,
    invalidate,
    onDocumentsChanged,
  } = actions;
  return (
    <>
      <ConfirmationDialog
        open={!!docPendingDelete}
        onClose={() => {
          if (!deleteMutation.isPending) setDocPendingDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Remove document?"
        body={
          <>
            <p className="tw:text-zinc-700">
              Are you sure you want to remove{' '}
              <span className="tw:font-semibold">
                {docPendingDelete?.label || docPendingDelete?.original_filename || 'this document'}
              </span>
              ?
            </p>
            {!!docPendingDelete?.signature && (
              <div className="tw:mt-3 tw:rounded-md tw:bg-stoplight-warning/10 tw:p-4">
                <div className="tw:flex">
                  <div className="tw:flex-shrink-0">
                    <i className="fa-solid fa-triangle-exclamation tw:text-stoplight-warning" />
                  </div>
                  <div className="tw:ml-3">
                    <p className="tw:text-sm tw:text-stoplight-warning">
                      Removing this document removes its signature history with it. If you want to
                      keep the signature history, replace the document instead.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </>
        }
        confirmText="Remove"
        confirmVariant="danger"
        isLoading={deleteMutation.isPending}
      />

      <DocumentEditDialog
        open={!!docEditing}
        doc={docEditing}
        onClose={() => {
          if (!patchDocumentMutation.isPending) setDocEditing(null);
        }}
        onSubmit={handleEditSubmit}
        isSubmitting={patchDocumentMutation.isPending}
      />

      <DocumentReplaceDialog
        open={!!docPendingReplace}
        doc={docPendingReplace}
        onClose={() => setDocPendingReplace(null)}
        onSuccess={() => {
          invalidate();
          onDocumentsChanged?.();
        }}
        isExpirable={!!docPendingReplace?.document_type?.is_expirable}
      />

      <DocumentSignDialog
        open={!!docSigning}
        doc={docSigning}
        onClose={() => setDocSigning(null)}
        onSuccess={(signatureRequest) => {
          const signedDoc = docSigning;
          invalidate();
          onDocumentsChanged?.();
          // When staff signs and the organization's signer slot is still open,
          // prompt to invite an org user to sign (per the signing design).
          if (signedDoc && shouldPromptOrgSigner(audience, signatureRequest)) {
            setDocRequestingSignature(signedDoc);
          }
        }}
      />

      <RequestSignatureDialog
        open={!!docRequestingSignature}
        signatureRequestId={docRequestingSignature?.signature?.id ?? null}
        healthcareEntityId={docRequestingSignature?.owner_id ?? ''}
        onClose={() => setDocRequestingSignature(null)}
        onSuccess={() => invalidate()}
      />
    </>
  );
}
